import { useOrganization } from '@clerk/clerk-react';
import React, { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LegacyCgForm as CgForm } from 'modules/Common/form/v2';
import { ConsultantOperatorInfoFormConfig } from 'modules/Common/config/ConsultantOperatorInfoFormConfig';
import { ConsultantInfoService } from 'modules/Consultant/api/ConsultantInfoService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import * as faIcons from '@fortawesome/free-solid-svg-icons';
import { apiErrorMessage } from 'helpers/serverIssues';

export default function ConsultantInfoForm({ onSubmit }) {
  const { t } = useTranslation('common');
  const formRef = useRef();
  const formGroups = ConsultantOperatorInfoFormConfig;

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [operator, setOperator] = useState(null);

  const { isLoaded, organization } = useOrganization();
  const [existingOperator, setExistingOperator] = useState(false);

  useEffect(() => {
    const init = async () => {
      setLoading(true);
      let res = await ConsultantInfoService.get();
      if (!res.success && organization) {
        let orgAddr = {
          additionalLine: '',
          city: '',
          country: '',
          POBox: '',
          postcode: '',
          state: '',
          street: '',
          number: '',
        };

        let newOperator = {
          name: organization.name,
          address: orgAddr,
        };
        setFormFromOperator(newOperator);
        setOperator(newOperator);
        setLoading(false);
        return;
      }
      setLoading(false);

      if (!res?.success) return;
      if (!formRef.current) return;
      setExistingOperator(true);
      let op = res.success;
      setFormFromOperator(op);
      setOperator(op);
    };

    init();
  }, [isLoaded]);

  const getOperatorInfoFormData = (formData) => {
    const newAddr = {
      ...operator.address,
      additionalLine: formData.additionalLine,
      city: formData.city,
      country: formData.country,
      POBox: formData.poBox,
      postcode: formData.postcode,
      state: formData.state,
      street: formData.street,
      number: formData.streetNumber,
    };

    const newOperator = {
      ...operator,
      name: formData.operatorName,
      address: newAddr,
      contactName: formData.contactName,
      contactEmail: formData.contactEmail,
      phoneNumber: formData.phoneNumber,
    };

    setOperator(newOperator);

    return newOperator;
  };

  const handleSubmit = async (formData) => {
    let newOperator = getOperatorInfoFormData(formData);

    setSubmitError(null);
    setSaving(true);
    let res;
    if (existingOperator) {
      res = await ConsultantInfoService.update(newOperator);
    } else {
      res = await ConsultantInfoService.create(newOperator);
    }
    setSaving(false);

    if (['OK', 'Created'].includes(res.statusText)) {
      formRef?.current?.showSubmitSuccess();
      if (onSubmit) {
        onSubmit(true);
      }
    }
    if (res.error) {
      setSubmitError(apiErrorMessage(res.error) || res.error?.response?.data?.error);
    }
  };

  const setFormFromOperator = (op) => {
    const updateFormValue = (name, value) => {
      formRef?.current?.updateField(name, value);
    };

    updateFormValue('operatorName', op.name);
    updateFormValue('country', op.address.country);
    updateFormValue('city', op.address.city);
    updateFormValue('phoneNumber', op.phoneNumber);
    updateFormValue('contactName', op.contactName);
    updateFormValue('contactEmail', op.contactEmail);
  };

  const isFormDisabled = () => {
    return loading || saving;
  };

  const getFormGroups = () => {
    return formGroups;
  };

  const isFormSubmitting = () => {
    return saving;
  };

  const getLoadingSpinner = () => {
    return loading ? (
      <FontAwesomeIcon
        icon={faIcons.faSpinner}
        className="mr-2 fa-spin"
      />
    ) : null;
  };

  const getSubmitErrorMessage = () => {
    return submitError ? (
      <div
        role="alert"
        className="relative mb-4 mt-2 rounded-md border px-4 py-3 bg-dangerSubtle text-dangerEmphasis border-dangerBorder"
      >
        <button
          type="button"
          onClick={() => setSubmitError(false)}
          aria-label={t('common:actions.close', 'Close')}
          className="absolute right-3 top-3 text-dangerEmphasis hover:opacity-70"
        >
          <FontAwesomeIcon icon={faIcons.faXmark} />
        </button>
        <h4 className="font-bold mb-1 pr-6">An error occurred</h4>
        <p className="m-0">{submitError}</p>
      </div>
    ) : null;
  };

  const getForm = () => {
    return (
      <CgForm
        ref={formRef}
        formGroups={getFormGroups()}
        disabled={isFormDisabled()}
        submitting={isFormSubmitting()}
        btnName={onSubmit ? 'Next' : 'Save'}
        onSubmit={(e) => handleSubmit(e)}
        loading={loading}
      />
    );
  };

  return (
    <div className="flex flex-col gap-2 mt-1">
      <h1>Consultant Information {getLoadingSpinner()}</h1>
      {getSubmitErrorMessage()}
      {getForm()}
    </div>
  );
}
